import { ComponentProps, memo, ReactNode } from 'react';

import { AppConfigProvider } from '@common/app-config';
import { UserAgentProvider } from '@common/device-manager';
import { TrackerProvider } from '@common/log-manager';
import { SiteId } from '@common/site-manager';
import { BrandStyleProvider } from '@common/styles';

import { QueryClientProvider } from '../_common/contexts/query-client-provider/QueryClientProvider';
import { ThemeProvider } from '../_common/contexts/theme-provider/ThemeProvider';

interface Props {
  /**
   * UserAgentProvider와 TrackerProvider에 전달 할 user-agent 설정
   */
  userAgent?: string;
  /**
   * ThemeProvider 설정
   *
   * - `"light"`, `"dark"` 등 고정 테마: ThemeProvider를 루트에 적용합니다.
   * - `"system"`: zds ThemeProvider를 렌더링하지 않습니다. 페이지별로 직접 적용하세요. legacy ThemeProvider 는 light 값을 사용합니다.
   */
  theme?: ComponentProps<typeof ThemeProvider>['theme'];
  /**
   * AppConfigProvider 설정
   */
  appConfig?: Omit<ComponentProps<typeof AppConfigProvider>, 'children'>;
  /**
   * BrandStyleProvider 설정
   * `@common/overlay-manager` 등에서 사이트별 스타일 적용에 활용됩니다
   */
  brandStyle?: Omit<ComponentProps<typeof BrandStyleProvider>, 'children'>;
  /**
   * TrackerProvider 설정
   */
  tracker?: { disabled?: boolean };
  children?: ReactNode;
}

/**
 * 전역 context provider로, 아래와 같은 기능을 제공합니다.
 *
 * ## context
 * - AppConfigProvider
 * - UserAgentProvider
 * - TrackerProvider
 * - ThemeProvider (ZDSThemeProvider, LegacyThemeProvider)
 * - BrandStyleProvider
 * - QueryClientProvider (@tanstack/react-query@v5, react-query@v3)
 *
 * ### 제공하지 않는 것들
 * - 상품카드 관련 컨텍스트(ProductCardProvider, LikeSubject)
 * - AndroidSafeAreaProvider
 * - 센트리 ErrorBoundary
 *
 * 글로벌 스타일과 Head 관련 기능은 `@common/components`의 `GlobalStyles`, `NextAppHead`를 사용하세요.
 *
 * @example
 * #### 기본적인 사용 (Next.js)
 * ```tsx
 * // pages/_app.tsx
 * import { RootContextProvider } from '@common/contexts';
 * import { GlobalStyles, NextAppHead } from '@common/components';
 *
 * function MyApp({ Component, pageProps }: AppProps) {
 *   const { userAgent } = pageProps;
 *
 *   return (
 *     <RootContextProvider userAgent={userAgent}>
 *       <NextAppHead />
 *       <GlobalStyles />
 *       <Component {...pageProps} />
 *     </RootContextProvider>
 *   );
 * }
 * ```
 *
 * #### 앱 설정과 SEO 포함
 * ```tsx
 * // pages/_app.tsx
 * import { RootContextProvider } from '@common/contexts';
 * import { GlobalStyles, NextAppHead } from '@common/components';
 *
 * function MyApp({ Component, pageProps }: AppProps) {
 *   const { userAgent } = pageProps;
 *
 *   return (
 *     <RootContextProvider
 *       userAgent={userAgent}
 *       appConfig={{
 *         zigzagUuid: pageProps.zigzagUuid,
 *         isZigzagApp: pageProps.isZigzagApp,
 *       }}
 *     >
 *       <NextAppHead seo={{ title: '지그재그', description: '여성쇼핑 No.1 지그재그' }} />
 *       <GlobalStyles />
 *       <Component {...pageProps} />
 *     </RootContextProvider>
 *   );
 * }
 * ```
 *
 * #### React SPA에서 사용 (zigzin 등)
 *
 * Next.js가 아닌 React SPA에서는 `NextAppHead`를 사용할 수 없습니다.
 * `GlobalStyles`와 `RootContextProvider`만 사용하세요.
 *
 * ```tsx
 * // App.tsx (React SPA)
 * import { RootContextProvider } from '@common/contexts';
 * import { GlobalStyles } from '@common/components';
 *
 * function App() {
 *   return (
 *     <RootContextProvider userAgent={navigator.userAgent}>
 *       <GlobalStyles />
 *       <RouterProvider router={router} />
 *     </RootContextProvider>
 *   );
 * }
 * ```
 *
 * #### 상품카드 컨텍스트와 함께 사용
 *
 * 상품카드 기능이 필요한 경우, `LikeSubject`와 `ProductCardProvider`를 children 내부에서 별도로 구성하세요.
 *
 * ```tsx
 * // pages/_app.tsx
 * import { RootContextProvider } from '@common/contexts';
 * import { GlobalStyles, NextAppHead } from '@common/components';
 * import { LikeSubject, useLikeObserve } from '@widgets/like-product';
 * import { ProductCardProvider } from '@widgets/product-card';
 *
 * function MyApp({ Component, pageProps }: AppProps) {
 *   const { userAgent } = pageProps;
 *
 *   return (
 *     <RootContextProvider userAgent={userAgent}>
 *       <NextAppHead />
 *       <GlobalStyles />
 *       <LikeSubject>
 *         <ProductCardProvider useLikeObserver={useLikeObserve}>
 *           <Component {...pageProps} />
 *         </ProductCardProvider>
 *       </LikeSubject>
 *     </RootContextProvider>
 *   );
 * }
 * ```
 */
export const RootContextProvider = memo(
  ({ userAgent = '', appConfig, brandStyle, theme = 'light', tracker, children }: Props) => {
    return (
      <AppConfigProvider {...appConfig}>
        <UserAgentProvider initialUserAgent={userAgent}>
          <TrackerProvider user_agent={userAgent} disabled={tracker?.disabled}>
            <ThemeProvider theme={theme}>
              <BrandStyleProvider {...brandStyle} site_id={brandStyle?.site_id ?? SiteId.ZIGZAG}>
                <QueryClientProvider>{children}</QueryClientProvider>
              </BrandStyleProvider>
            </ThemeProvider>
          </TrackerProvider>
        </UserAgentProvider>
      </AppConfigProvider>
    );
  },
);
